import React from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTheme} from "../../../../../connectui/theme/ThemeProvider";
import {buildStyles} from "../../../../../connectui/component/buildStyles";
import Text from "../../../../../connectui/component/display/Text";
import DropdownExpand from "../../../../../connectui/component/display/dropdown/DropdownExpand";
import FlexContainer from "../../../../../connectui/component/layout/FlexContainer";
import ICONS from "connectui/icons/iconMap";
import Container from "../../../../../connectui/component/layout/Container";
import {
  getInboxData,
  updateGuest as updGuest,
} from "redux/actions/guestsActions";
import TextInput from "../../../../../connectui/component/input/text/type/TextInput";
import OutlinedInputBase from "../../../../../connectui/component/input/text/OutlinedInputBase";
import SvgIcon from "../../../../../connectui/component/display/SvgIcon";
import ButtonIcon from "connectui/component/input/button/ButtonIcon";
import {useTranslation} from "react-i18next";

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function NotesSection({guestId}) {
  const {theme, isMobile} = useTheme();
  const dispatch = useDispatch();
  const guests = useSelector((state) => state.defaultReducer.guests_dict);
  const {t} = useTranslation();
  const [guest, setGuest] = React.useState(guests[guestId]);
  const [expanded, setExpanded] = React.useState(true);
  const [description, setDescription] = React.useState(
    guest?.description || "",
  );
  const updatingNotes = React.useRef(false);
  const currentRequest = React.useRef(null);

  React.useEffect(() => {
    setGuest(guests[guestId]);
  }, [guests, guestId]);

  React.useEffect(() => {
    setDescription(guest?.description || "");
    updatingNotes.current = false;
  }, [guest]);

  const updateGuest = React.useCallback(
    (field, val) => {
      dispatch(
        updGuest({
          guest_id: guest.guest_id,
          field,
          val,
          onSuccess: () => {
            dispatch(getInboxData());

            if (currentRequest.current) {
              updatingNotes.current = false;
            }
          },
        }),
      );
    },
    [dispatch, guest?.guest_id],
  );

  const handleDescriptionChange = (e) => {
    const val = e.target.value;
    if (!updatingNotes.current) {
      updatingNotes.current = true;
    }
    setDescription(val);
    const requestId = Date.now().toString(36);
    currentRequest.current = requestId;
    sleep(1500).then(() => {
      if (requestId !== currentRequest.current) return;
      updateGuest("description", val);
    });
  };

  return (
    <DropdownExpand
      divider={false}
      expanded={expanded}
      onExpand={() => setExpanded(!expanded)}
      customLabel={
        <FlexContainer alignItems="center" gap={theme.spacing(2)}>
          <Text variant={"textLG"} weight="semibold">
            {t("inbox.guest-details.notes.header")}
          </Text>
        </FlexContainer>
      }
    >
      <FlexContainer flexDirection={"column"} gap={theme.spacing(3)}>
        {guest?.metadata?.ai_summary && (
          <Container sx={{width: "100%"}}>
            <FlexContainer
              flexDirection="row"
              alignItems="center"
              gap={theme.spacing(1)}
            >
              <SvgIcon
                component={ICONS.ai}
                sx={{
                  color: theme.colors.purple500,
                  width: "14px",
                  height: "14px",
                }}
              />
              <Text variant={"textSM"} weight="medium">
                {t("inbox.guest-details.notes.conversation-summary")}
              </Text>
            </FlexContainer>
            <Text variant="textSM" sx={{maxHeight: "120px", overflow: "auto"}}>
              {guest?.metadata?.ai_summary || ""}
            </Text>
          </Container>
        )}
        <Container sx={{width: "100%", marginTop: theme.spacing(1)}}>
          <FlexContainer
            flexDirection="row"
            alignItems="center"
            gap={theme.spacing(1)}
          >
            <SvgIcon
              component={ICONS.note}
              sx={{
                color: theme.colors.textSecondary,
                width: "14px",
                height: "14px",
              }}
            />
            <Text variant={"textSM"} weight="medium">
              {t("inbox.guest-details.notes.guest-notes")}
            </Text>
          </FlexContainer>
          <TextInput
            sx={{
              marginTop: theme.spacing(1),
              boxShadow: "none",
              border: `1px solid ${theme.colors.border}`,
            }}
            as="textarea"
            inputBase={OutlinedInputBase}
            inputProps={{
              disabled: false,
              value: description,
              onChange: handleDescriptionChange,
              style: {
                minHeight: 62,
                color: theme.colors.textSecondary,
                backgroundColor: theme.colors.white,
                cursor: "text",
                ...theme.typography.textSM.medium,
              },
            }}
          />
        </Container>
      </FlexContainer>
    </DropdownExpand>
  );
}
